// @flow strict

import * as React from 'react'
import cx from 'classnames'
import { SectionThemes, SectionWrapper } from '../../../SectionWrapper'
import SubtitleSection from '../../../SubtitleSection'

import { type PropsWithChildren } from '@mondough/public-web-components'
import styles from './CustomerProfileTriptych.module.scss'

const Chevron = () => (
  <span>
    <svg width="10" height="18" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M.293 16.293a1 1 0 001.414 1.414l8-8a1 1 0 000-1.414l-8-8A1 1 0 00.293 1.707L7.586 9 .293 16.293z"
        fill="#14233C"
        fillRule="evenodd"
      />
    </svg>
  </span>
)

type Item = {|
  image: string,
  quote: string,
  customer: {|
    name: string,
    tagline: string,
    location: string,
    industry: string,
    links: $ReadOnlyArray<{|
      title: string,
      url: string,
    |}>,
  |},
|}

type CustomerProfileTriptychProps = {|
  items: $ReadOnlyArray<Item>,
|}

export const CustomerProfileTriptych = ({
  items,
  children,
}: PropsWithChildren<
  CustomerProfileTriptychProps,
  React.Element<typeof SubtitleSection>,
>) => {
  const [currentItem, setCurrentItem] = React.useState(0)

  const scrollContainer = React.useRef<?HTMLElement>(null)
  const navigate = (direction: string) => {
    const itemWidth =
      scrollContainer.current && scrollContainer.current.offsetWidth

    let nextItemIndex =
      direction === 'previous' ? currentItem + -1 : currentItem + 1

    if (nextItemIndex >= 3) {
      nextItemIndex = 0
    } else if (nextItemIndex < 0) {
      nextItemIndex = 3 - 1
    }

    if (scrollContainer !== null && itemWidth) {
      /** $FlowIgnore
       *
       * Although it has very good browser support, scrollLeft is part of the
       * CSSOM View Module which is still (as of March 2020) a draft proposal
       * https://drafts.csswg.org/cssom-view/#dom-element-scrollleft */
      scrollContainer.current.scrollLeft = itemWidth * nextItemIndex
    }

    setCurrentItem(nextItemIndex)
  }

  // This watches the users' scroll to ensure that the `currentItem` state
  // is updated when a user scrolls through the carousel rather than uses the
  // navigation buttons.
  //
  // If they scroll to the second item, and then begin using the navigation
  // items, they should be taken to the next item in sequence.
  React.useEffect(() => {
    const el: ?HTMLElement = scrollContainer.current

    if (!el) {
      return
    }

    const itemWidth = window && window.innerWidth

    let isScrolling = false

    function onScroll(ev: Event) {
      window.clearTimeout(isScrolling)

      isScrolling = setTimeout(() => {
        const scrollPosition = el.scrollLeft
        setCurrentItem(Math.floor(scrollPosition / itemWidth))
      }, 66)
    }

    el.addEventListener('scroll', onScroll)

    return () => el.removeEventListener('scroll', onScroll)
  }, [scrollContainer])

  return (
    <SectionWrapper
      theme={SectionThemes.GREY}
      contentClassName={styles.baseContent}
      className={cx(styles.content, {
        [styles.contentWithSubtitle]: children != null,
      })}
    >
      {children}
      <div className={styles.root} role="region">
        <div className={styles.scrollContainer} ref={scrollContainer}>
          {items.map(item => (
            <div key={item.image} className={styles.itemWrapper}>
              <img className={styles.photo} src={item.image} alt="" />
              {item.customer != null && (
                <div className={styles.feature}>
                  <blockquote className={styles.quote}>{item.quote}</blockquote>
                  <p className={styles.customerName}>{item.customer.name}</p>
                  <p className={styles.customerInfo}>{item.customer.tagline}</p>
                  <p className={styles.customerInfo}>
                    {item.customer.industry} <span aria-hidden="true">|</span>{' '}
                    {item.customer.location}
                  </p>
                  {item.customer.links && item.customer.links.length > 0 && (
                    <p className={styles.customerLinks}>
                      {item.customer.links.map(link => (
                        <a key={link.url} href={link.url}>
                          {link.title}
                        </a>
                      ))}
                    </p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
        <div className={styles.controlGroup}>
          <button
            className={cx(styles.control, styles.previous)}
            onClick={() => navigate('previous')}
            aria-label="Previous"
          >
            <Chevron />
          </button>
          <button
            className={cx(styles.control, styles.next)}
            onClick={() => navigate('next')}
            aria-label="Next"
          >
            <Chevron />
          </button>
        </div>
      </div>
    </SectionWrapper>
  )
}

export default CustomerProfileTriptych
